import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { MapPin, TrendingUp, Building2, Ruler, LandPlot, FileCheck } from 'lucide-react';
export function PropertyHighlights() {
  const stats = [{
    label: 'Land Area',
    value: '280',
    unit: 'm²'
  }, {
    label: 'Building',
    value: '420',
    unit: 'm²'
  }, {
    label: 'Zoning',
    value: 'C1Z',
    unit: ''
  }, {
    label: 'Levels',
    value: '3',
    unit: ''
  }];
  const highlights = [{
    icon: <MapPin className="w-7 h-7 text-brand-gold" strokeWidth={1.5} data-id="element-104" />,
    title: 'Prime Location',
    description: "Situated in the heart of Richmond, one of Melbourne's most dynamic and sought-after commercial precincts with exceptional connectivity."
  }, {
    icon: <TrendingUp className="w-7 h-7 text-brand-gold" strokeWidth={1.5} data-id="element-105" />,
    title: 'Investment Grade',
    description: 'A rare opportunity to acquire a premium commercial asset in a high-growth corridor with strong tenant demand and capital appreciation.'
  }, {
    icon: <Building2 className="w-7 h-7 text-brand-gold" strokeWidth={1.5} data-id="element-106" />,
    title: 'Lifestyle Precinct',
    description: "Steps from Bridge Road boutiques, Victoria Street dining, and Melbourne's premier sporting and entertainment venues."
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="property" className="bg-brand-black relative" data-id="element-107">
      {/* Key Stats Bar */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.8
    }} className="border-y border-white/10 bg-brand-surface" data-id="element-108">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4" data-id="element-109">
          {stats.map((stat, i) => <div key={i} className={`py-8 md:py-10 text-center ${i < stats.length - 1 ? 'border-r border-white/5' : ''}`} data-id="element-110">
              <p className="text-brand-muted text-xs tracking-widest uppercase mb-2" data-id="element-111">
                {stat.label}
              </p>
              <p className="font-heading text-4xl md:text-5xl text-white" data-id="element-112">
                {stat.value}
                {stat.unit && <span className="text-brand-gold text-2xl ml-1" data-id="element-113">
                    {stat.unit}
                  </span>}
              </p>
            </div>)}
        </div>
      </motion.div>

      {/* Highlights */}
      <div className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-12" data-id="element-114">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.8
      }} className="mb-16 md:mb-20 flex flex-col md:flex-row md:items-end justify-between gap-6" data-id="element-115">
          <div data-id="element-116">
            <h2 className="font-heading text-5xl md:text-6xl text-white mb-4 uppercase" data-id="element-117">
              The Opportunity
            </h2>
            <div className="w-12 h-0.5 bg-brand-gold" data-id="element-118"></div>
          </div>
          <p className="text-brand-muted max-w-md font-light text-sm leading-relaxed" data-id="element-119">
            A premium commercial offering in Melbourne's most coveted inner-city
            precinct, presenting exceptional value for owner-occupiers and
            investors alike.
          </p>
        </motion.div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        margin: '-100px'
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-24" data-id="element-120">
          {highlights.map((item, index) => <motion.div key={index} variants={itemVariants} className="bg-brand-surface p-8 md:p-10 border-t-2 border-brand-gold/40 hover:border-brand-gold transition-colors duration-500 group" data-id="element-121">
              <div className="transform group-hover:-translate-y-1 transition-transform duration-500" data-id="element-122">
                <div className="mb-6" data-id="element-123">{item.icon}</div>
                <h3 className="font-heading text-2xl md:text-3xl text-white mb-3 uppercase" data-id="element-124">
                  {item.title}
                </h3>
                <p className="text-brand-muted leading-relaxed font-light text-sm" data-id="element-125">
                  {item.description}
                </p>
              </div>
            </motion.div>)}
        </motion.div>

        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 1
      }} className="relative py-14 text-center border-y border-white/5" data-id="element-126">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-6 bg-brand-gold" data-id="element-127"></div>
          <h3 className="font-heading text-3xl md:text-5xl lg:text-6xl text-brand-gold leading-tight max-w-4xl mx-auto uppercase" data-id="element-128">
            Richmond — Melbourne's #1 Inner-City Commercial Hub
          </h3>
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-px h-6 bg-brand-gold" data-id="element-129"></div>
        </motion.div>
      </div>
    </section>;
}